import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router";
import { useCan } from "../../context/AuthContext";
import { ccApi } from "../../lib/ccApi";
import { useOnShown } from "../../lib/inApp";
import { waitingRows } from "../../views/cc/home/waiting";

/** Fired after the owner decides a need or a lesson, so the counts on the tabs catch up. */
export const BADGES_CHANGED = "fz-badges-changed";

export function refreshBadges(): void {
  window.dispatchEvent(new Event(BADGES_CHANGED));
}

export interface Badges {
  needs: number;
  lessons: number;
}

/** How long a count stays good while the owner moves between screens. */
const FRESH_MS = 30_000;

/**
 * What waits for the owner, for the tabs and the rail: the rows of Needs you (needs, plans to review, goals
 * getting ready) and the lessons that wait for a call. Read on arrival, when the app comes back to the front,
 * when the phone app shows this screen again, after a decision, and at most every 30 seconds while moving
 * between screens.
 */
export function useBadges(on: boolean): Badges {
  const [counts, setCounts] = useState<Badges>({ needs: 0, lessons: 0 });
  const { pathname } = useLocation();
  const last = useRef(0);
  const can = useCan();

  const load = useCallback(() => {
    last.current = Date.now();
    ccApi
      .home()
      .then((h) => setCounts({ needs: waitingRows(h, can).length, lessons: can("editor") ? h.lessonsWaiting : 0 }))
      .catch(() => {
        // Why: a count is a hint; a failed read keeps the last one rather than showing an error on the tab bar.
      });
  }, [can]);

  useEffect(() => {
    if (!on) return;
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener(BADGES_CHANGED, load);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener(BADGES_CHANGED, load);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [on, load]);

  useEffect(() => {
    if (on && Date.now() - last.current > FRESH_MS) load();
  }, [on, pathname, load]);

  useOnShown(() => {
    if (on) load();
  });

  return counts;
}
